import React, { useState, useEffect, useRef } from 'react';
import {
  Mail,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Send,
  Eye,
  EyeOff,
  Wifi,
  WifiOff,
  Settings,
  AlertCircle,
} from 'lucide-react';
import { useWidgetTheme, mainAppTheme } from '../widget/useWidgetTheme';
import { SmtpConfig, SmtpGatewayCoreProps } from '../widget/types';

type SmtpModeValue = 'default' | 'custom' | 'byos' | 'disabled';

const EMPTY_FORM: SmtpConfig = {
  SMTPHost: '',
  SMTPPort: '587',
  SMTPFromAddress: '',
  SMTPRequiredSecurity: 'True',
  SMTPUserName: '',
  SMTPPassword: '',
  SMTPEnableSSL: 'True',
};

interface ConfirmModalState {
  action: 'default' | 'disabled';
}

const SmtpGatewayCore: React.FC<SmtpGatewayCoreProps> = ({
  smtpMode,
  config,
  configError,
  loading,
  error,
  mode,
  theme: themeOverrides,
  userEmail,
  onSave,
  onReset,
  onDisable,
  onTest,
  onRefresh,
  onSuccess,
  onError,
}) => {
  const widgetTheme = useWidgetTheme();
  const baseTheme = mode === 'modal' ? mainAppTheme : widgetTheme;
  const theme = themeOverrides ? { ...baseTheme, ...themeOverrides } : baseTheme;

  const [selectedMode, setSelectedMode] = useState<SmtpModeValue>(smtpMode);
  const [form, setForm] = useState<SmtpConfig>(EMPTY_FORM);
  const [formDirty, setFormDirty] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [testRecipient, setTestRecipient] = useState(userEmail || '');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const confirmInputRef = useRef<HTMLInputElement>(null);

  // Sync selected mode when smtpMode prop changes (e.g. after save)
  useEffect(() => { setSelectedMode(smtpMode); }, [smtpMode]);

  // FIX: Pre-fill form when config loads, but only if user hasn't touched the form yet
  useEffect(() => {
    if (
      (smtpMode === 'custom' || smtpMode === 'byos') &&
      config?.SMTPHost &&
      !formDirty
    ) {
      setForm({
        SMTPHost: config.SMTPHost || '',
        SMTPPort: config.SMTPPort || '587',
        SMTPFromAddress: config.SMTPFromAddress || '',
        SMTPRequiredSecurity: config.SMTPRequiredSecurity || 'True',
        SMTPUserName: config.SMTPUserName || '',
        SMTPPassword: '', // never pre-fill password
        SMTPEnableSSL: config.SMTPEnableSSL || 'True',
      });
    }
  }, [config]);

  // Reset form when user switches mode tabs
  useEffect(() => {
    if (selectedMode === 'custom' || selectedMode === 'byos') {
      setForm({
        SMTPHost: config?.SMTPHost || '',
        SMTPPort: config?.SMTPPort || '587',
        SMTPFromAddress: config?.SMTPFromAddress || '',
        SMTPRequiredSecurity: config?.SMTPRequiredSecurity || 'True',
        SMTPUserName: config?.SMTPUserName || '',
        SMTPPassword: '',
        SMTPEnableSSL: config?.SMTPEnableSSL || 'True',
      });
      setConfirmPassword('');
      setFormDirty(false);
      setTestResult(null);
    }
  }, [selectedMode]);

  // Focus confirm input when modal opens
  useEffect(() => {
    if (confirmModal) {
      setConfirmText('');
      setTimeout(() => confirmInputRef.current?.focus(), 50);
    }
  }, [confirmModal]);

  const handleFieldChange = (field: keyof SmtpConfig, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFormDirty(true);
    setTestResult(null);
  };

  const handleTest = async () => {
    if (!testRecipient.trim()) { onError?.('Please enter a test recipient email'); return; }
    if (!form.SMTPPassword) { onError?.('Password is required to test SMTP'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const result = await onTest(form, testRecipient.trim());
      setTestResult(result);
      if (result.success) onSuccess?.('Test email sent successfully');
    } catch (err: any) {
      setTestResult({ success: false, error: err.message || 'Test failed' });
    } finally { setTesting(false); }
  };

  const handleSave = async () => {
    if (!testResult?.success) return;
    if (form.SMTPPassword !== confirmPassword) { onError?.('Passwords do not match'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onSuccess?.('SMTP settings saved');
      setTestResult(null);
      setFormDirty(false);
    } catch (err: any) {
      onError?.(err.message || 'Failed to save SMTP settings');
    } finally { setSaving(false); }
  };

  // Intercept mode switches — show modal if currently on custom/byos (destructive change)
  const handleSelectMode = (newMode: SmtpModeValue) => {
    if (newMode === selectedMode) return;
    setSelectedMode(newMode);
  };

  const handleRequestDefault = () => {
    if (smtpMode === 'custom' || smtpMode === 'byos') {
      setConfirmModal({ action: 'default' });
    } else {
      executeDefault();
    }
  };

  const handleRequestDisabled = () => {
    if (smtpMode === 'custom' || smtpMode === 'byos') {
      setConfirmModal({ action: 'disabled' });
    } else {
      executeDisabled();
    }
  };

  const executeDefault = async () => {
    setApplying(true);
    try {
      await onReset();
      onSuccess?.('Switched to DashboardFox default SMTP');
    } catch (err: any) {
      onError?.(err.message || 'Failed to switch to default SMTP');
      setSelectedMode(smtpMode);
    } finally { setApplying(false); }
  };

  const executeDisabled = async () => {
    setApplying(true);
    try {
      await onDisable();
      onSuccess?.('SMTP disabled');
    } catch (err: any) {
      onError?.(err.message || 'Failed to disable SMTP');
      setSelectedMode(smtpMode);
    } finally { setApplying(false); }
  };

  const handleConfirmProceed = async () => {
    if (confirmText !== 'RESET') return;
    const action = confirmModal!.action;
    setConfirmModal(null);
    if (action === 'default') {
      await executeDefault();
    } else {
      await executeDisabled();
    }
  };

  const canSave = testResult?.success === true && form.SMTPPassword === confirmPassword && form.SMTPPassword.length > 0;
  const hasConfig = !!(config?.SMTPHost);
  const isByos = smtpMode === 'byos';

  const modeOptions: Array<{
    value: SmtpModeValue;
    label: string;
    description: string;
    icon: React.ReactNode;
    activeColor: string;
    activeBg: string;
  }> = [
    {
      value: 'default',
      label: 'Default Gateway',
      description: 'Use DashboardFox shared email',
      icon: <Wifi size={18} strokeWidth={1.5} />,
      activeColor: theme.colors.success,
      activeBg: theme.colors.successBg,
    },
    {
      value: 'custom',
      label: 'Custom SMTP',
      description: 'Use your own mail server',
      icon: <Settings size={18} strokeWidth={1.5} />,
      activeColor: '#3b82f6',
      activeBg: '#eff6ff',
    },
    {
      value: 'disabled',
      label: 'Disabled',
      description: 'No outbound email',
      icon: <WifiOff size={18} strokeWidth={1.5} />,
      activeColor: '#6b7280',
      activeBg: '#f3f4f6',
    },
  ];

  // ── Confirm Modal ────────────────────────────────────────────────────────────
  const renderConfirmModal = () => {
    if (!confirmModal) return null;
    const isDefault = confirmModal.action === 'default';
    const confirmed = confirmText === 'RESET';

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px',
      }}>
        <div style={{
          backgroundColor: '#fff', borderRadius: theme.radius.lg,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          width: '100%', maxWidth: '480px',
          padding: '28px',
        }}>
          {/* Modal header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '20px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: theme.radius.md,
              backgroundColor: '#fef3c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <AlertCircle size={20} strokeWidth={1.5} color="#d97706" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700, color: theme.colors.text }}>
                {isDefault ? 'Switch to Default Gateway?' : 'Disable SMTP?'}
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: theme.colors.textMuted }}>
                This will reconfigure your workspace email delivery.
              </p>
            </div>
          </div>

          {/* Current config summary */}
          {hasConfig && (
            <div style={{
              backgroundColor: theme.colors.bgAlt,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.md,
              padding: '14px 16px',
              marginBottom: '16px',
            }}>
              <p style={{ margin: '0 0 8px', fontSize: '0.75rem', fontWeight: 600, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Current Custom SMTP
              </p>
              <p style={{ margin: '0 0 4px', fontSize: '0.875rem', color: theme.colors.text, fontFamily: theme.fontMono }}>{config!.SMTPHost}:{config!.SMTPPort}</p>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: theme.colors.textMuted }}>{config!.SMTPFromAddress}</p>
            </div>
          )}

          {/* Consequence */}
          <div style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: theme.radius.md,
            padding: '14px 16px',
            marginBottom: '20px',
          }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e', fontWeight: 500 }}>
              {isDefault
                ? 'Your custom SMTP settings will be removed from the pod. All future emails will be sent via the DashboardFox shared gateway (reports@mail.dashboardfox.app). Any in-progress email jobs will be affected.'
                : 'All outbound email from this workspace will be disabled immediately. Scheduled reports and alert notifications will stop delivering until email is re-enabled.'}
            </p>
          </div>

          {/* Confirm input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: theme.colors.text, marginBottom: '8px' }}>
              Type <strong style={{ fontFamily: theme.fontMono, color: '#dc2626' }}>RESET</strong> to confirm
            </label>
            <input
              ref={confirmInputRef}
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && confirmed) handleConfirmProceed(); }}
              placeholder="RESET"
              style={{
                width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                border: `2px solid ${confirmText.length > 0 && !confirmed ? theme.colors.error : theme.colors.border}`,
                borderRadius: theme.radius.md,
                fontSize: '0.9375rem', fontFamily: theme.fontMono,
                color: theme.colors.text,
                outline: 'none',
                letterSpacing: '0.05em',
              }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleConfirmProceed}
              disabled={!confirmed || applying}
              style={{
                flex: 1, padding: '10px 20px',
                backgroundColor: confirmed && !applying ? '#dc2626' : theme.colors.border,
                color: confirmed && !applying ? '#fff' : theme.colors.textMuted,
                border: 'none', borderRadius: theme.radius.md,
                fontSize: '0.875rem', fontWeight: 600,
                cursor: (!confirmed || applying) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                transition: 'background 0.15s',
              }}
            >
              {applying
                ? <><Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} /> Applying...</>
                : isDefault ? 'Switch to Default Gateway' : 'Disable SMTP'
              }
            </button>
            <button
              onClick={() => { setConfirmModal(null); setSelectedMode(smtpMode); }}
              disabled={applying}
              style={{
                padding: '10px 20px',
                backgroundColor: theme.colors.bg, color: theme.colors.textMuted,
                border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md,
                fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };
  // ────────────────────────────────────────────────────────────────────────────

  if (error && !loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', backgroundColor: theme.colors.errorBg, border: `1px solid ${theme.colors.error}`, borderRadius: theme.radius.md, padding: '16px' }}>
        <AlertTriangle size={20} strokeWidth={1.5} color={theme.colors.error} style={{ flexShrink: 0 }} />
        <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: theme.colors.error, flex: 1 }}>{error}</p>
        <button onClick={onRefresh} style={{ padding: '6px 12px', backgroundColor: theme.colors.error, color: '#fff', border: 'none', borderRadius: theme.radius.sm, fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }

  return (
    <>
      {renderConfirmModal()}
      <div style={{ fontFamily: theme.fontSans, color: theme.colors.text }}>
        {/* Header */}
        <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: theme.radius.lg, background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 6px -1px rgba(59,130,246,0.3)', flexShrink: 0 }}>
            <Mail size={24} strokeWidth={1.5} color="#fff" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em', color: theme.colors.text }}>SMTP Gateway</h1>
            <p style={{ margin: 0, fontSize: '0.875rem', color: theme.colors.textTertiary }}>Configure outbound email delivery</p>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.textMuted, padding: '24px 0' }}>
            <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '0.875rem' }}>Loading SMTP settings...</span>
          </div>
        ) : (
          <>
            {/* Mode Selector — hidden for byos */}
            {!isByos && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
                {modeOptions.map(opt => {
                  const isActive = smtpMode === opt.value;
                  const isSelected = selectedMode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleSelectMode(opt.value)}
                      style={{ padding: '16px', backgroundColor: isSelected ? opt.activeBg : theme.colors.bg, border: `2px solid ${isSelected ? opt.activeColor : theme.colors.border}`, borderRadius: theme.radius.lg, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s ease' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: isSelected ? opt.activeColor : theme.colors.textMuted }}>{opt.icon}</span>
                        {isActive && (
                          <span style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.05em', color: opt.activeColor, backgroundColor: opt.activeBg, padding: '2px 8px', borderRadius: '9999px', border: `1px solid ${opt.activeColor}` }}>
                            ACTIVE
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: isSelected ? opt.activeColor : theme.colors.text, marginBottom: '2px' }}>{opt.label}</div>
                      <div style={{ fontSize: '0.8125rem', color: theme.colors.textMuted }}>{opt.description}</div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* DEFAULT panel */}
            {selectedMode === 'default' && (
              <div style={{ backgroundColor: theme.colors.bg, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', borderRadius: theme.radius.md, backgroundColor: theme.colors.successBg, marginBottom: smtpMode !== 'default' ? '20px' : '0' }}>
                  <CheckCircle size={18} strokeWidth={1.5} color={theme.colors.success} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <div>
                    <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: theme.colors.success }}>DashboardFox Shared Email Gateway</p>
                    <p style={{ margin: '2px 0 0', fontSize: '0.8125rem', color: theme.colors.success, opacity: 0.85 }}>
                      Emails will be sent from <strong>reports@mail.dashboardfox.app</strong>
                    </p>
                  </div>
                </div>
                {smtpMode !== 'default' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
                    <button onClick={handleRequestDefault} disabled={applying} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', backgroundColor: theme.colors.success, color: '#fff', border: 'none', borderRadius: theme.radius.md, fontSize: '0.875rem', fontWeight: 600, cursor: applying ? 'not-allowed' : 'pointer', opacity: applying ? 0.7 : 1 }}>
                      {applying ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={14} strokeWidth={2} />}
                      {applying ? 'Applying...' : 'Switch to Default Gateway'}
                    </button>
                    <button onClick={() => setSelectedMode(smtpMode)} style={{ padding: '10px 16px', backgroundColor: theme.colors.bg, color: theme.colors.textMuted, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                  </div>
                )}
              </div>
            )}

            {/* DISABLED panel */}
            {selectedMode === 'disabled' && (
              <div style={{ backgroundColor: theme.colors.bg, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', borderRadius: theme.radius.md, backgroundColor: '#fffbeb', marginBottom: smtpMode !== 'disabled' ? '20px' : '0' }}>
                  <AlertTriangle size={18} strokeWidth={1.5} color="#f59e0b" style={{ flexShrink: 0, marginTop: '1px' }} />
                  <div>
                    <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#92400e' }}>SMTP Disabled</p>
                    <p style={{ margin: '2px 0 0', fontSize: '0.8125rem', color: '#92400e', opacity: 0.9 }}>
                      No outbound email will be sent from this workspace. Scheduled reports and alerts will not deliver.
                    </p>
                  </div>
                </div>
                {smtpMode !== 'disabled' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
                    <button onClick={handleRequestDisabled} disabled={applying} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', backgroundColor: '#6b7280', color: '#fff', border: 'none', borderRadius: theme.radius.md, fontSize: '0.875rem', fontWeight: 600, cursor: applying ? 'not-allowed' : 'pointer', opacity: applying ? 0.7 : 1 }}>
                      {applying ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} /> : <WifiOff size={14} strokeWidth={2} />}
                      {applying ? 'Applying...' : 'Disable SMTP'}
                    </button>
                    <button onClick={() => setSelectedMode(smtpMode)} style={{ padding: '10px 16px', backgroundColor: theme.colors.bg, color: theme.colors.textMuted, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                  </div>
                )}
              </div>
            )}

            {/* CUSTOM / BYOS panel */}
            {(selectedMode === 'custom' || isByos) && (
              <div style={{ backgroundColor: theme.colors.bg, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: '24px' }}>
                {isByos && (
                  <div style={{ padding: '12px 16px', marginBottom: '20px', backgroundColor: '#fffbeb', borderRadius: theme.radius.sm, borderLeft: '4px solid #f59e0b' }}>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400e' }}>
                      <strong>Your plan requires your own SMTP gateway.</strong> DashboardFox shared email is not available on your plan.
                    </p>
                  </div>
                )}

                {configError && (
                  <div style={{ padding: '12px 16px', marginBottom: '20px', backgroundColor: '#fef3c7', borderRadius: theme.radius.sm, borderLeft: '4px solid #f59e0b' }}>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400e' }}>Could not load current SMTP config from pod.</p>
                  </div>
                )}

                {/* Current config read-only summary */}
                {hasConfig && !configError && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px', padding: '16px', backgroundColor: theme.colors.bgAlt, borderRadius: theme.radius.md, border: `1px solid ${theme.colors.border}` }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <span style={{ fontSize: '0.75rem', color: theme.colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Configuration</span>
                    </div>
                    {[
                      { label: 'SMTP Host', value: config!.SMTPHost },
                      { label: 'Port', value: config!.SMTPPort },
                      { label: 'From Address', value: config!.SMTPFromAddress },
                      { label: 'Username', value: config!.SMTPUserName },
                      { label: 'TLS', value: config!.SMTPRequiredSecurity === 'True' ? 'Enabled' : 'Disabled' },
                      { label: 'SSL', value: config!.SMTPEnableSSL === 'True' ? 'Enabled' : 'Disabled' },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <span style={{ fontSize: '0.75rem', color: theme.colors.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
                        <p style={{ margin: '2px 0 0', fontSize: '0.875rem', fontWeight: 500, color: theme.colors.text, fontFamily: theme.fontMono }}>{value}</p>
                      </div>
                    ))}
                  </div>
                )}

                <h3 style={{ margin: '0 0 20px', fontSize: '1rem', fontWeight: 600, color: theme.colors.text }}>
                  {hasConfig ? 'Update SMTP Configuration' : 'Configure SMTP Gateway'}
                </h3>

                {hasConfig && (
                  <div style={{ padding: '10px 14px', marginBottom: '20px', backgroundColor: '#eff6ff', borderRadius: theme.radius.sm, borderLeft: '4px solid #3b82f6' }}>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#1d4ed8' }}>
                      Fields are pre-filled from your current config. Enter a new password and run a test to apply changes.
                    </p>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ gridColumn: '1 / 2' }}>
                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: theme.colors.textMuted, marginBottom: '6px' }}>SMTP Host *</label>
                    <input type="text" value={form.SMTPHost} onChange={e => handleFieldChange('SMTPHost', e.target.value)} placeholder="smtp.example.com"
                      style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', backgroundColor: theme.colors.bgAlt, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', color: theme.colors.text, fontFamily: theme.fontMono, outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: theme.colors.textMuted, marginBottom: '6px' }}>Port *</label>
                    <input type="number" value={form.SMTPPort} onChange={e => handleFieldChange('SMTPPort', e.target.value)} placeholder="587"
                      style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', backgroundColor: theme.colors.bgAlt, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', color: theme.colors.text, fontFamily: theme.fontMono, outline: 'none' }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: theme.colors.textMuted, marginBottom: '6px' }}>From Address *</label>
                    <input type="email" value={form.SMTPFromAddress} onChange={e => handleFieldChange('SMTPFromAddress', e.target.value)} placeholder="reports@yourdomain.com"
                      style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', backgroundColor: theme.colors.bgAlt, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', color: theme.colors.text, outline: 'none' }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: theme.colors.textMuted, marginBottom: '6px' }}>Username *</label>
                    <input type="text" value={form.SMTPUserName} onChange={e => handleFieldChange('SMTPUserName', e.target.value)} placeholder="smtp-username"
                      style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', backgroundColor: theme.colors.bgAlt, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', color: theme.colors.text, outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: theme.colors.textMuted, marginBottom: '6px' }}>
                      Password *{hasConfig && <span style={{ fontWeight: 400, color: theme.colors.textMuted }}> — required to apply changes</span>}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPassword ? 'text' : 'password'} value={form.SMTPPassword} onChange={e => handleFieldChange('SMTPPassword', e.target.value)} placeholder={hasConfig ? 'Enter password to update' : 'SMTP password'}
                        style={{ width: '100%', padding: '10px 40px 10px 12px', boxSizing: 'border-box', backgroundColor: theme.colors.bgAlt, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', color: theme.colors.text, outline: 'none' }} />
                      <button onClick={() => setShowPassword(p => !p)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textMuted, padding: 0 }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: theme.colors.textMuted, marginBottom: '6px' }}>Confirm Password *</label>
                    <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => { setConfirmPassword(e.target.value); setTestResult(null); }} placeholder="Confirm password"
                      style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', backgroundColor: theme.colors.bgAlt, border: `1px solid ${confirmPassword && form.SMTPPassword !== confirmPassword ? theme.colors.error : theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', color: theme.colors.text, outline: 'none' }} />
                    {confirmPassword && form.SMTPPassword !== confirmPassword && (
                      <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: theme.colors.error }}>Passwords do not match</p>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.colors.text }}>Use TLS</label>
                    <button onClick={() => handleFieldChange('SMTPRequiredSecurity', form.SMTPRequiredSecurity === 'True' ? 'False' : 'True')} style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', backgroundColor: form.SMTPRequiredSecurity === 'True' ? theme.colors.primary : theme.colors.border, position: 'relative', transition: 'background 0.2s' }}>
                      <div style={{ position: 'absolute', top: '2px', left: form.SMTPRequiredSecurity === 'True' ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#fff', transition: 'left 0.2s' }} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.colors.text }}>Enable SSL</label>
                    <button onClick={() => handleFieldChange('SMTPEnableSSL', form.SMTPEnableSSL === 'True' ? 'False' : 'True')} style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', backgroundColor: form.SMTPEnableSSL === 'True' ? theme.colors.primary : theme.colors.border, position: 'relative', transition: 'background 0.2s' }}>
                      <div style={{ position: 'absolute', top: '2px', left: form.SMTPEnableSSL === 'True' ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#fff', transition: 'left 0.2s' }} />
                    </button>
                  </div>
                </div>

                {/* Test Send */}
                <div style={{ borderTop: `1px solid ${theme.colors.border}`, paddingTop: '20px', marginTop: '4px' }}>
                  <h4 style={{ margin: '0 0 4px', fontSize: '0.9375rem', fontWeight: 600, color: theme.colors.text }}>Send Test Email</h4>
                  <p style={{ margin: '0 0 12px', fontSize: '0.8125rem', color: theme.colors.textMuted }}>A successful test is required before saving.</p>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: theme.colors.textMuted, marginBottom: '6px' }}>Send test to:</label>
                      <input type="email" value={testRecipient} onChange={e => setTestRecipient(e.target.value)} placeholder="recipient@example.com"
                        style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', backgroundColor: theme.colors.bgAlt, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', color: theme.colors.text, outline: 'none' }} />
                    </div>
                    <button onClick={handleTest} disabled={testing || !form.SMTPPassword || !form.SMTPHost}
                      style={{ marginTop: '22px', display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', backgroundColor: theme.colors.bgAlt, color: theme.colors.text, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, fontSize: '0.875rem', fontWeight: 500, cursor: (testing || !form.SMTPPassword || !form.SMTPHost) ? 'not-allowed' : 'pointer', opacity: (testing || !form.SMTPPassword || !form.SMTPHost) ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                      {testing ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} strokeWidth={2} />}
                      {testing ? 'Sending...' : 'Send Test'}
                    </button>
                  </div>
                  {testResult && (
                    <div style={{ marginTop: '12px', padding: '12px 16px', borderRadius: theme.radius.md, backgroundColor: testResult.success ? theme.colors.successBg : theme.colors.errorBg, border: `1px solid ${testResult.success ? theme.colors.success : theme.colors.error}`, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      {testResult.success
                        ? <CheckCircle size={16} strokeWidth={2} color={theme.colors.success} style={{ flexShrink: 0, marginTop: '1px' }} />
                        : <AlertTriangle size={16} strokeWidth={2} color={theme.colors.error} style={{ flexShrink: 0, marginTop: '1px' }} />}
                      <div>
                        <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: testResult.success ? theme.colors.success : theme.colors.error }}>
                          {testResult.success ? 'Test email sent successfully' : 'Test failed'}
                        </p>
                        {testResult.error && <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: theme.colors.error, fontFamily: theme.fontMono }}>{testResult.error}</p>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Save */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '20px', borderTop: `1px solid ${theme.colors.border}`, paddingTop: '20px', alignItems: 'center' }}>
                  <button onClick={handleSave} disabled={!canSave || saving}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 24px', backgroundColor: canSave && !saving ? theme.colors.success : theme.colors.border, color: canSave && !saving ? '#fff' : theme.colors.textMuted, border: 'none', borderRadius: theme.radius.md, fontSize: '0.875rem', fontWeight: 600, cursor: (!canSave || saving) ? 'not-allowed' : 'pointer' }}>
                    {saving ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={14} strokeWidth={2} />}
                    {saving ? 'Saving...' : 'Save SMTP Settings'}
                  </button>
                  {!testResult?.success && (
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: theme.colors.textMuted }}>Send a successful test to enable save</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SmtpGatewayCore;
